/**
 * Employee Directory Help Content
 *
 * Covers people search, team directory, profiles, and people & teams management.
 */

import type { HelpContent } from '../types';
import {
  createHelpContent,
  createSection,
  createFAQ,
  createTip,
  createRelatedLink,
} from '../utils';

// =============================================================================
// MAIN DIRECTORY HELP
// =============================================================================

export const directoryHelp: HelpContent = createHelpContent({
  id: 'directory',
  title: 'Employee Directory',
  description: 'Find and connect with colleagues across the organization',
  lastUpdated: '2026-02-08',
  version: '2.0',

  tips: [
    createTip(
      'tip-search',
      'pro-tip',
      'Advanced Search',
      'Search by name, department, role, or skills. You can combine terms like "engineering manager" to narrow results.'
    ),
  ],

  sections: [
    createSection(
      'overview',
      'Overview',
      `<p>The Employee Directory helps you find and connect with colleagues across the organization. Search by name, department, skills, or role to find the right person.</p>
      <p>Key features:</p>
      <ul>
        <li><strong>People Search</strong> — Find colleagues by name, role, or skill</li>
        <li><strong>Team Directory</strong> — Browse the organizational structure by teams</li>
        <li><strong>Employee Profiles</strong> — View contact info, reporting lines, and skills</li>
        <li><strong>Org Chart</strong> — Visual organizational hierarchy</li>
      </ul>`
    ),
    createSection(
      'searching',
      'People Search',
      `<p>Use the search bar to find colleagues by:</p>
      <ul>
        <li><strong>Name</strong> — First or last name (partial matches supported)</li>
        <li><strong>Department</strong> — Filter by team or department</li>
        <li><strong>Role</strong> — Search by job title</li>
        <li><strong>Skills</strong> — Find experts in specific areas</li>
      </ul>
      <p>Results appear instantly as you type. Click on a person to view their full profile.</p>`
    ),
    createSection(
      'teams',
      'Team Directory',
      `<p>Browse the organization by team structure. Each team shows:</p>
      <ul>
        <li>Team name and description</li>
        <li>Team lead</li>
        <li>Number of members</li>
        <li>Member list with roles</li>
      </ul>
      <p>Click on a team to see all its members and sub-teams.</p>`
    ),
    createSection(
      'profiles',
      'Employee Profiles',
      `<p>Each employee profile shows:</p>
      <ul>
        <li><strong>Contact Information</strong> — Email, phone (if shared)</li>
        <li><strong>Department & Team</strong> — Where they sit in the organization</li>
        <li><strong>Reporting Structure</strong> — Who they report to and their direct reports</li>
        <li><strong>Skills & Expertise</strong> — Listed competencies</li>
        <li><strong>Current Projects</strong> — Active project assignments</li>
      </ul>`
    ),
    createSection(
      'org-chart',
      'Organization Chart',
      '<p>The org chart provides a visual tree view of the reporting hierarchy. Navigate up and down the tree to understand the organizational structure. Click on any node to view that person\'s profile.</p>'
    ),
  ],

  faqs: [
    createFAQ(
      'faq-update-profile',
      'How do I update my profile information?',
      'You can update your profile from your account settings. Some fields like department and job title may require HR or admin approval to change.',
      ['update', 'profile', 'edit', 'change']
    ),
    createFAQ(
      'faq-contact-hidden',
      'Why can\'t I see someone\'s contact details?',
      'Some employees may have chosen to keep certain contact information private. You can usually reach them via internal chat or email through the platform.',
      ['privacy', 'contact', 'hidden', 'phone']
    ),
    createFAQ(
      'faq-skills',
      'How do skills get listed on profiles?',
      'Skills can be self-declared by the employee and may also be added by managers or HR. Completing training courses may automatically add relevant skills.',
      ['skills', 'expertise', 'competency', 'how']
    ),
    createFAQ(
      'faq-team-membership',
      'How do I join a team?',
      'Navigate to People & Teams and request to join a team. The team lead will review and approve your request. Your manager can also assign you to teams.',
      ['join', 'team', 'membership', 'add']
    ),
    createFAQ(
      'faq-reporting',
      'Who sets reporting lines?',
      'Reporting lines are managed by HR and admin users. If your reporting manager is incorrect, contact HR to update it.',
      ['reporting', 'manager', 'hierarchy', 'line']
    ),
  ],

  relatedLinks: [
    createRelatedLink('People & Teams', '/people-teams', { description: 'Manage team membership and join requests' }),
    createRelatedLink('Admin', '/admin', { description: 'User management (admins only)' }),
    createRelatedLink('Training', '/training', { description: 'Find courses to build new skills' }),
  ],

  keywords: [
    'directory',
    'people',
    'employees',
    'contacts',
    'org chart',
    'staff',
    'search',
    'team',
    'profile',
    'skills',
  ],
});

// =============================================================================
// SECTION-LEVEL HELP
// =============================================================================

export const directoryPeopleTeamsHelp: HelpContent = createHelpContent({
  id: 'directory:people-teams',
  title: 'People & Teams',
  description: 'Manage teams, team membership, and join requests',
  parentId: 'directory',
  sections: [
    createSection(
      'overview',
      'Overview',
      '<p>The People & Teams page lets you browse teams, manage membership, and handle join requests. Teams are the primary organizational unit for collaboration and resource planning.</p>'
    ),
    createSection(
      'teams-tab',
      'Teams Tab',
      `<p>Browse all teams in the organization. Each team card shows the team name, lead, member count, and description. Click a team to see its full member list.</p>`
    ),
    createSection(
      'people-tab',
      'People Tab',
      '<p>View all employees with their team assignments. Filter by department or search by name to find specific people.</p>'
    ),
    createSection(
      'join-requests',
      'Join Requests',
      `<p>Request to join a team or manage incoming requests:</p>
      <ul>
        <li><strong>Requesting</strong> — Click "Request to Join" on any team. The team lead will be notified.</li>
        <li><strong>Approving</strong> — Team leads see pending requests and can approve or decline them.</li>
      </ul>`
    ),
  ],
  faqs: [
    createFAQ('faq-1', 'Can I be on multiple teams?', 'Yes, employees can be members of multiple teams. Your primary team is set by HR/admin.', ['multiple', 'teams', 'many']),
    createFAQ('faq-2', 'How do I create a new team?', 'Team creation is typically managed by admins or managers. Contact your manager or HR to request a new team.', ['create', 'new', 'team']),
    createFAQ('faq-3', 'What does a team lead do?', 'Team leads manage team membership, approve join requests, and may have visibility into team analytics and resource allocation.', ['lead', 'role', 'responsibilities']),
  ],
  keywords: ['teams', 'people', 'membership', 'join', 'requests', 'organization'],
});
